import { useAtomValue } from 'jotai/index';
import {
  BrokerNerdsBarkerCoreModelsNotificationsUserNotification,
  getGetApiNotificationsActiveLatestQueryKey,
  getGetApiNotificationsLatestQueryKey,
  getGetApiNotificationsUnreadCountQueryKey,
  putApiNotificationsNotificationIdArchive,
  putApiNotificationsNotificationIdRead,
  putApiNotificationsNotificationIdUnarchive,
  useGetApiNotificationsActiveLatest,
  useGetApiNotificationsLatest,
  useGetApiNotificationsUnreadCount,
} from '../../api';
import { useGlobalState } from '../../data/Global.state';
import { useCallback, useMemo, useState } from 'react';
import yasml from '@thirtytech/yasml';
import { useDisclosure } from '@mantine/hooks';
import { AxiosResponse } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { auth } from '../../data/atoms.auth';

// Sort newest-first by createdAt. Uses a decorate-sort-undecorate (Schwartzian
// transform) so each createdAt is parsed by dayjs exactly once (O(n) parses)
// instead of once per comparison (O(n log n) parses). Result is identical to
// the previous inline comparators: the decorated keys equal the old
// `dayjs(x.createdAt).unix()` values, the sort is descending by that key, and
// because Array.prototype.sort is stable the relative order of equal-second
// timestamps is preserved exactly as before.
function sortByCreatedAtDesc<T extends { createdAt: Date | string }>(items: T[]): T[] {
  return items
    .map((item) => ({ item, key: dayjs(item.createdAt).unix() }))
    .sort((a, b) => b.key - a.key)
    .map((decorated) => decorated.item);
}

function NotificationsState() {
  const queryClient = useQueryClient();
  const { selectedTenantIds } = useGlobalState('selectedTenantIds');
  const [isMenuOpened, { toggle: toggleMenu, close: closeMenu }] = useDisclosure(false);
  const apiToken = useAtomValue(auth.apiTokenAtom);
  const [activeTab, setActiveTab] = useState<string | null>('inbox'); // ['inbox', 'all', 'archived'
  const { data: notificationCount } = useGetApiNotificationsUnreadCount({
    query: {
      enabled: !!apiToken && selectedTenantIds?.length > 0,
      refetchOnWindowFocus: true,
      select(data) {
        return data.data;
      },
    },
  });

  const { data: inboxNotifications, refetch: refetchInboxNotifications } = useGetApiNotificationsActiveLatest({
    query: {
      enabled: !!apiToken && selectedTenantIds?.length > 0,
      structuralSharing: false,
      select(data) {
        return data.data.sort((a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix());
      },
    },
  });

  const { data: allNotifications, refetch: refetchAllNotifications } = useGetApiNotificationsLatest({
    query: {
      enabled: !!apiToken && selectedTenantIds?.length > 0,
      structuralSharing: false,
      select(data) {
        return data.data.sort((a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix());
      },
    },
  });

  const archivedNotifications = useMemo(() => {
    if (!allNotifications) {
      return [];
    }

    return allNotifications.filter((notification) => notification.archivedAt !== null);
  }, [allNotifications]);

  const archiveNotifications = useCallback(
    async (notifications: BrokerNerdsBarkerCoreModelsNotificationsUserNotification[]) => {
      await Promise.all(
        notifications.map(({ notificationId, tenantId }) =>
          putApiNotificationsNotificationIdArchive(notificationId, {
            headers: {
              'x-tenant-id': tenantId,
            },
          }),
        ),
      );
      await queryClient.invalidateQueries({ queryKey: getGetApiNotificationsActiveLatestQueryKey() });
      await queryClient.invalidateQueries({ queryKey: getGetApiNotificationsLatestQueryKey() });
      await queryClient.invalidateQueries({ queryKey: getGetApiNotificationsUnreadCountQueryKey() });
    },
    [queryClient],
  );

  const handleRead = useCallback(
    async (notification: BrokerNerdsBarkerCoreModelsNotificationsUserNotification) => {
      await putApiNotificationsNotificationIdRead(notification.notificationId, {
        headers: {
          'x-tenant-id': notification.tenantId,
        },
      });
      queryClient.setQueryData<AxiosResponse<number>>(getGetApiNotificationsUnreadCountQueryKey(), (oldData) => {
        if (!oldData || !oldData?.data) {
          return oldData;
        }

        return {
          ...oldData,
          data: oldData.data - 1,
        };
      });

      queryClient.setQueryData<AxiosResponse<BrokerNerdsBarkerCoreModelsNotificationsUserNotification[]>>(getGetApiNotificationsLatestQueryKey(), (oldData) => {
        if (!oldData || !oldData?.data) {
          return oldData;
        }

        const _notification = oldData.data.find((d) => d.notificationId === notification.notificationId);

        if (_notification === undefined) {
          return oldData;
        }

        _notification.readAt = new Date();

        return {
          ...oldData,
          // Operates on a freshly built array, so this is equivalent to the
          // previous inline `.sort(comparator)` (same new-array semantics, same
          // descending-by-createdAt order, same stable tie handling).
          data: sortByCreatedAtDesc([...oldData.data.filter((d) => d.notificationId !== notification.notificationId), _notification]),
        };
      });

      queryClient.setQueryData<AxiosResponse<BrokerNerdsBarkerCoreModelsNotificationsUserNotification[]>>(getGetApiNotificationsActiveLatestQueryKey(), (oldData) => {
        if (!oldData || !oldData?.data) {
          return oldData;
        }

        const _notification = oldData.data.find((d) => d.notificationId === notification.notificationId);

        if (_notification === undefined) {
          return oldData;
        }

        _notification.readAt = new Date();

        return {
          ...oldData,
          data: sortByCreatedAtDesc([...oldData.data.filter((d) => d.notificationId !== notification.notificationId), _notification]),
        };
      });
    },
    [queryClient],
  );

  const handleArchive = useCallback(
    async (notification: BrokerNerdsBarkerCoreModelsNotificationsUserNotification) => {
      await putApiNotificationsNotificationIdArchive(notification.notificationId, {
        headers: {
          'x-tenant-id': notification.tenantId,
        },
      });

      queryClient.setQueryData<AxiosResponse<BrokerNerdsBarkerCoreModelsNotificationsUserNotification[]>>(getGetApiNotificationsActiveLatestQueryKey(), (oldData) => {
        if (!oldData || !oldData?.data) {
          return oldData;
        }

        const _notification = oldData.data.find((d) => d.notificationId === notification.notificationId);

        if (_notification === undefined) {
          return oldData;
        }

        _notification.archivedAt = new Date();

        queryClient.setQueryData<AxiosResponse<BrokerNerdsBarkerCoreModelsNotificationsUserNotification[]>>(getGetApiNotificationsLatestQueryKey(), (allNotificationsUpdater) => {
          if (!allNotificationsUpdater || !allNotificationsUpdater?.data) {
            return allNotificationsUpdater;
          }

          return {
            ...allNotificationsUpdater,
            data: sortByCreatedAtDesc([...allNotificationsUpdater.data.filter((d) => d.notificationId !== notification.notificationId), _notification]),
          };
        });

        if (_notification.readAt === null) {
          queryClient.setQueryData<AxiosResponse<number>>(getGetApiNotificationsUnreadCountQueryKey(), (updater) => {
            if (updater === undefined) {
              return updater;
            }

            return {
              ...updater,
              data: updater.data - 1,
            };
          });
        }

        return {
          ...oldData,
          data: sortByCreatedAtDesc(oldData.data.filter((d) => d.notificationId !== notification.notificationId)),
        };
      });
    },
    [queryClient],
  );

  const handleUnarchive = useCallback(
    async (notification: BrokerNerdsBarkerCoreModelsNotificationsUserNotification) => {
      await putApiNotificationsNotificationIdUnarchive(notification.notificationId, {
        headers: {
          'x-tenant-id': notification.tenantId,
        },
      });
      queryClient.setQueryData<AxiosResponse<BrokerNerdsBarkerCoreModelsNotificationsUserNotification[]>>(getGetApiNotificationsLatestQueryKey(), (allNotificationsUpdater) => {
        if (!allNotificationsUpdater || !allNotificationsUpdater?.data) {
          return allNotificationsUpdater;
        }

        const _notification = allNotificationsUpdater.data.find((d) => d.notificationId === notification.notificationId);

        if (!_notification) {
          return allNotificationsUpdater;
        }

        _notification.archivedAt = null;

        queryClient.setQueryData<AxiosResponse<BrokerNerdsBarkerCoreModelsNotificationsUserNotification[]>>(
          getGetApiNotificationsActiveLatestQueryKey(),
          (activeNotificationsUpdater) => {
            if (!activeNotificationsUpdater || !activeNotificationsUpdater?.data || !_notification) {
              return activeNotificationsUpdater;
            }

            return {
              ...activeNotificationsUpdater,
              data: [...activeNotificationsUpdater.data.filter((d) => d.notificationId !== notification.notificationId), _notification],
            };
          },
        );

        if (_notification?.readAt === null) {
          queryClient.setQueryData<AxiosResponse<number>>(getGetApiNotificationsUnreadCountQueryKey(), (oldData) => {
            if (oldData === undefined) {
              return oldData;
            }

            return {
              ...oldData,
              data: oldData.data + 1,
            };
          });
        }

        return {
          ...allNotificationsUpdater,
          data: sortByCreatedAtDesc([...allNotificationsUpdater.data.filter((d) => d.notificationId !== notification.notificationId), _notification]),
        };
      });
    },
    [queryClient],
  );

  return {
    notificationCount,
    isMenuOpened,
    toggleMenu,
    closeMenu,
    activeTab,
    setActiveTab,
    inboxNotifications,
    allNotifications,
    handleRead,
    handleArchive,
    handleUnarchive,
    archiveNotifications,
    archivedNotifications,
    refetchInboxNotifications,
    refetchAllNotifications,
  };
}

export const { Provider: NotificationsProvider, useSelector: useNotifications } = yasml(NotificationsState);
